import React, { createContext, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { useSnackbar } from 'notistack'
import { useResErrors } from 'lib/hooks'
import { CollectionService } from 'lib/api/services/collection.service'
import { addMonths, differenceInCalendarMonths, format, parseISO } from 'date-fns'
import { buildMultipart } from 'lib/utils/mutiFormHelpers'

const DEBUG = true
const log = (...a) => DEBUG && console.log('[ApplicationStore]', ...a)
const err = (...a) => console.error('[ApplicationStore]', ...a)

const paginationInitialState = { count: 0, limit: 30, offset: 0 }
const applicationInitialState = null
const deferralInitialState = {
  deferralType: '',
  commonReason: '',
  suspension: { startDate: null, termMonths: '', reason: '', otherReason: '', files: {} },
  writeOff: { reason: '' },
  buyback: { car: { brand: '', model: '', plate: '', vin: '' }, files: {} }
}

export const ApplicationStoreContext = createContext({
  applicationStore: {},
  applicationStoreFunc: {}
})

const fmt = d => {
  if (!d) return undefined
  const date = d instanceof Date ? d : new Date(d)
  return format(date, 'yyyy-MM-dd')
}

const docTypeForSlot = slot => {
  const key = String(slot).split('.').pop()
  switch (key) {
    case 'application':
      return 'APPLICATION_FORM'
    case 'workCertificate':
      return 'EMPLOYMENT_CERTIFICATE'
    case 'incomeProof':
      return 'INCOME_LOSS_PROOF'
    case 'inspectionReport':
      return 'CAR_INSPECTION_REPORT'
    case 'buybackConsent':
      return 'BUYBACK_CONSENT'
    default:
      return 'OTHER'
  }
}

const fileMetaPathByType = {
  SUSPENSION: 'suspension.files',
  BUYBACK: 'buyback.files',
  WRITE_OFF: 'writeOff.files'
}

const buildFilesMeta = (filesObj = {}) =>
  Object.entries(filesObj)
    .filter(([, f]) => f?.name && (f?.raw || f?.base64))
    .map(([slot, f]) => ({ documentType: docTypeForSlot(slot), fileName: f.name }))

const setByPath = (obj, path, value) => {
  const keys = String(path).split('.')
  const next = { ...(obj || {}) }
  let cur = next
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i]
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...(cur[k] || {}) }
    cur = cur[k]
  }
  cur[keys[keys.length - 1]] = value
  return next
}

const base64ToBlob = b64 => {
  try {
    const bin = atob(b64)
    const u8 = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i)
    return new Blob([u8], { type: 'application/octet-stream' })
  } catch {
    return null
  }
}

const mapReasonToBackend = (reason, otherText) => {
  if (reason === 'Потерял место работы') return 'Утрата дохода'
  if (reason === 'Другие') return otherText || 'Другие'
  return reason || 'Другие'
}

const ApplicationStoreProvider = ({ children, autoFetch = true }) => {
  const { id } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { standardCheck } = useResErrors()

  const [pagination, setPagination] = useState(paginationInitialState)
  const [applications, setApplications] = useState([])
  const [application, setApplication] = useState(applicationInitialState)
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(false)
  const [resubmitErrors, setResubmitErrors] = useState({})
  const [deferral, setDeferral] = useState(deferralInitialState)

  useEffect(() => {
    if (!autoFetch) return
    log('init', { autoFetch, id })
    if (id) getApplicationById(id)
  }, [id])

  const getApplications = async (params = {}) => {
    log('getApplications:start', { params, pagination })
    setLoading(true)
    try {
      const query = { limit: pagination.limit, offset: pagination.offset, ...params }
      const res = await CollectionService.getApplications(query)
      standardCheck(res, () => {
        const list = res?.data?.list || []
        setApplications(list)
        setPagination(prev => ({ ...prev, count: Number(res?.data?.count || list.length) }))
        log('getApplications:success', { count: Number(res?.data?.count || list.length) })
      })
    } finally {
      setLoading(false)
      log('getApplications:done')
    }
  }

  const getApplicationById = async appId => {
    if (!appId) {
      log('getApplicationById:skip', { appId })
      return
    }
    setLoading(true)
    try {
      log('getApplicationById:start', { appId })
      const res = await CollectionService.getApplicationById(appId)
      standardCheck(res, () => {
        const data = res?.data || null
        setApplication(data)
        setFiles(data?.files || [])
        log('getApplicationById:success', {
          id: data?.id,
          type: data?.applicationType || data?.type,
          status: data?.status
        })
      })
    } finally {
      setLoading(false)
      log('getApplicationById:done', { appId })
    }
  }

  const getByPath = (obj, path) =>
    String(path)
      .split('.')
      .reduce((o, k) => (o ? o[k] : undefined), obj)

  const validateResubmitFields = (fieldDefs, formData) => {
    const errors = (fieldDefs || []).reduce((acc, f) => {
      const value = getByPath(formData, f.name)
      const ok = typeof f.validate === 'function' ? f.validate(value) : true
      if (!ok) acc[f.name] = f.error || true
      return acc
    }, {})
    setResubmitErrors(errors)
    if (Object.keys(errors).length) log('validateResubmitFields:fail', errors)
    return Object.keys(errors).length === 0
  }

  const clearResubmitError = name => setResubmitErrors(prev => ({ ...prev, [name]: undefined }))

  const clearAllResubmitErrors = () => setResubmitErrors({})

  const buildResubmitFormFromApplication = app => {
    const deferralType = app?.type || 'SUSPENSION'
    log('buildResubmitFormFromApplication', { deferralType })

    if (deferralType === 'SUSPENSION') {
      const s = app?.suspension || {}
      const from = s?.suspendFrom ? parseISO(s.suspendFrom) : s?.startDate ? new Date(s.startDate) : null
      const until = s?.suspendUntil ? parseISO(s.suspendUntil) : null
      const term =
        Number(s?.termMonths) || (from && until ? Math.max(1, differenceInCalendarMonths(until, from) || 1) : '')

      return {
        deferralType: 'SUSPENSION',
        suspension: {
          startDate: from || null,
          termMonths: term,
          reason: s?.reason || '',
          otherReason: s?.otherReason || '',
          files: {}
        }
      }
    }

    if (deferralType === 'BUYBACK') {
      const b = app?.buyback || {}
      return {
        deferralType: 'BUYBACK',
        buyback: {
          car: {
            brand: b?.car?.brand || '',
            model: b?.car?.model || '',
            plate: b?.car?.plate || '',
            vin: b?.car?.vin || ''
          },
          files: {}
        }
      }
    }

    if (deferralType === 'WRITE_OFF') {
      const w = app?.writeOff || {}
      return {
        deferralType: 'WRITE_OFF',
        writeOff: { reason: w?.reason || '' }
      }
    }

    return { deferralType: 'SUSPENSION', suspension: { files: {} } }
  }

  const buildSuspensionApplicationFormData = workListId => {
    const { deferralType, suspension } = deferral || {}
    if (deferralType !== 'SUSPENSION') return null
    const submitReason = (deferral?.commonReason || '').trim() || undefined

    const start = suspension?.startDate instanceof Date ? suspension.startDate : new Date(suspension?.startDate)
    const until = addMonths(start, Number(suspension?.termMonths || 0))

    const requestJson = {
      type: 'SUSPENSION',
      workListId: Number(workListId),
      submitReason,
      suspension: {
        reason: mapReasonToBackend(suspension?.reason, suspension?.otherReason),
        suspendFrom: format(start, 'yyyy-MM-dd'),
        suspendUntil: format(until, 'yyyy-MM-dd'),
        notificationDate: format(new Date(), 'yyyy-MM-dd'),
        files: Object.entries(suspension?.files || {})
          .filter(([, f]) => f?.name && (f?.raw || f?.base64))
          .map(([slot, f]) => ({
            documentType: docTypeForSlot(slot),
            fileName: f.name
          }))
      }
    }

    const fd = new FormData()
    fd.append('request', new Blob([JSON.stringify(requestJson)], { type: 'application/json' }))

    Object.values(suspension?.files || {}).forEach(f => {
      if (!f?.name) return
      if (f?.raw) {
        fd.append('files', f.raw, f.name)
      } else if (f?.base64) {
        const blob = base64ToBlob(f.base64)
        if (blob) fd.append('files', blob, f.name)
      }
    })

    return fd
  }

  const buildBuybackApplicationFormData = workListId => {
    const { deferralType, buyback } = deferral || {}
    if (deferralType !== 'BUYBACK') return null
    const submitReason = (deferral?.commonReason || '').trim() || undefined

    const requestJson = {
      type: 'BUYBACK',
      workListId: Number(workListId),
      submitReason,
      buyback: {
        car: {
          brand: buyback?.car?.brand || '',
          model: buyback?.car?.model || '',
          plate: buyback?.car?.plate || '',
          vin: buyback?.car?.vin || ''
        },
        files: Object.entries(buyback?.files || {})
          .filter(([, f]) => f?.name && (f?.raw || f?.base64))
          .map(([slot, f]) => ({
            documentType: docTypeForSlot(slot),
            fileName: f.name
          }))
      }
    }

    const fd = new FormData()
    fd.append('request', new Blob([JSON.stringify(requestJson)], { type: 'application/json' }))

    Object.values(buyback?.files || {}).forEach(f => {
      if (!f?.name) return
      if (f?.raw) {
        fd.append('files', f.raw, f.name)
      } else if (f?.base64) {
        const blob = base64ToBlob(f.base64)
        if (blob) fd.append('files', blob, f.name)
      }
    })

    return fd
  }

  const buildWriteOffApplicationFormData = workListId => {
    const { deferralType, writeOff } = deferral || {}
    if (deferralType !== 'WRITE_OFF') return null
    const submitReason = (deferral?.commonReason || '').trim() || undefined

    const requestJson = {
      type: 'WRITE_OFF',
      workListId: Number(workListId),
      submitReason,
      writeOff: {
        reason: writeOff?.reason || ''
      }
    }

    const fd = new FormData()
    fd.append('request', new Blob([JSON.stringify(requestJson)], { type: 'application/json' }))

    return fd
  }

  const resubmitApplication = async (appId, override = {}) => {
    const useId = appId ?? application?.id
    if (!useId) {
      log('resubmitApplication:skip:noId', { appId, applicationId: application?.id })
      return { success: false }
    }

    const type = override?.type || application?.type || override?.applicationType || application?.applicationType
    if (!type) {
      log('resubmitApplication:skip:noType', { overrideType: override?.type, appType: application?.type })
      return { success: false }
    }

    let formFromState = {}
    if (type === 'SUSPENSION') {
      const s = override?.form || application?.suspension || {}
      formFromState = {
        reason: s?.reason,
        otherReason: s?.otherReason,
        startDate: s?.startDate || s?.suspendFrom,
        termMonths: s?.termMonths,
        suspendFrom: s?.suspendFrom,
        suspendUntil: s?.suspendUntil,
        files: override?.form?.files || {}
      }
    } else if (type === 'BUYBACK') {
      const b = override?.form || application?.buyback || {}
      formFromState = { car: { ...(b?.car || {}) }, files: override?.form?.files || {} }
    } else if (type === 'WRITE_OFF') {
      const w = override?.form || application?.writeOff || {}
      formFromState = { reason: w?.reason, files: override?.form?.files || {} }
    }

    setLoading(true)
    try {
      const reqJson = (function buildResubmitRequestJson({ type, form, comment }) {
        if (type === 'SUSPENSION') {
          const start = form?.startDate || form?.suspendFrom
          const suspendFrom = fmt(start)
          let suspendUntil = form?.suspendUntil ? fmt(form?.suspendUntil) : undefined
          if (!suspendUntil && form?.termMonths) {
            const base = start instanceof Date ? start : new Date(start)
            suspendUntil = fmt(addMonths(base, Number(form?.termMonths)))
          }
          return {
            comment: comment || 'Переотправка',
            suspension: {
              reason: form?.reason === 'Другие' ? form?.otherReason || 'Другие' : form?.reason || 'Другие',
              suspendFrom,
              suspendUntil,
              notificationDate: fmt(new Date()),
              files: buildFilesMeta(form?.files)
            }
          }
        }
        if (type === 'BUYBACK') {
          return {
            comment: comment || 'Переотправка',
            buyback: {
              car: {
                brand: form?.car?.brand,
                model: form?.car?.model,
                plate: form?.car?.plate,
                vin: form?.car?.vin
              },
              files: buildFilesMeta(form?.files)
            }
          }
        }
        if (type === 'WRITE_OFF') {
          return {
            comment: comment || 'Переотправка',
            writeOff: { reason: form?.reason, files: buildFilesMeta(form?.files) }
          }
        }
        return { comment: comment || 'Переотправка' }
      })({
        type,
        form: formFromState,
        comment: override?.comment || 'Переотправка'
      })

      log('resubmitApplication:submit', { useId, type })
      const fd = buildMultipart({
        json: reqJson,
        jsonKey: 'request',
        jsonMetaPath: fileMetaPathByType[type],
        slotFiles: formFromState?.files,
        mapSlotToMeta: (slot, file) => ({ documentType: docTypeForSlot(slot), fileName: file.name })
      })

      const res = await CollectionService.resubmitApplicationMultipart(useId, fd)
      if (res?.data) {
        log('resubmitApplication:success', { useId })
        enqueueSnackbar('Заявка отправлена на пересмотр', { variant: 'success' })
        if (application?.id === useId) await getApplicationById(useId)
        await getApplications()
        return { success: true }
      }
      log('resubmitApplication:fail:noData', { useId })
      enqueueSnackbar('Ошибка при повторной отправке', { variant: 'error' })
      return { success: false }
    } catch (e) {
      err('resubmitApplication:error', e)
      enqueueSnackbar('Ошибка при повторной отправке', { variant: 'error' })
      return { success: false }
    } finally {
      setLoading(false)
      log('resubmitApplication:done', { useId })
    }
  }

  const approveApplication = async appId => {
    const useId = appId ?? application?.id
    if (!useId) {
      log('approveApplication:skip:noId', { appId, applicationId: application?.id })
      return { success: false }
    }
    setLoading(true)
    try {
      log('approveApplication:submit', { useId })
      const res = await CollectionService.completeApplication(useId)
      if (res?.data) {
        enqueueSnackbar('Заявка согласована', { variant: 'success' })
        if (application?.id === useId) await getApplicationById(useId)
        await getApplications()
        return { success: true }
      } else {
        enqueueSnackbar('Ошибка согласования', { variant: 'error' })
        return { success: false }
      }
    } catch {
      err('approveApplication:error')
      enqueueSnackbar('Ошибка отправки запроса', { variant: 'error' })
      return { success: false }
    } finally {
      setLoading(false)
      log('approveApplication:done', { useId })
    }
  }

  const rejectApplication = async (reason, appId) => {
    const useId = appId ?? application?.id
    const trimmed = (reason || '').trim()
    if (!useId) {
      log('rejectApplication:skip:noId', { appId, applicationId: application?.id })
      return { success: false }
    }
    if (!trimmed) {
      log('rejectApplication:skip:noReason')
      return { success: false, error: 'REASON_REQUIRED' }
    }

    setLoading(true)
    try {
      log('rejectApplication:submit', { useId })
      const res = await CollectionService.rejectApplication(useId, trimmed)
      if (res?.data) {
        enqueueSnackbar('Заявка отклонена', { variant: 'success' })
        if (application?.id === useId) await getApplicationById(useId)
        await getApplications()
        log('rejectApplication:success', { useId })
        return { success: true }
      } else {
        log('rejectApplication:fail:noData', { useId })
        enqueueSnackbar('Ошибка отклонения', { variant: 'error' })
        return { success: false }
      }
    } catch {
      err('rejectApplication:error')
      enqueueSnackbar('Ошибка отклонения', { variant: 'error' })
      return { success: false }
    } finally {
      setLoading(false)
      log('rejectApplication:done', { useId })
    }
  }

  const handlePaginationChange = (e, page) => {
    const newOffset = Math.max(0, Number(page) - 1)
    setPagination(prev => ({ ...prev, offset: newOffset }))
  }

  const handleDeferralChange = e => {
    log('handleDeferralChange', { name: e?.target?.name })
    const { name, value } = e.target
    setDeferral(prev => ({ ...prev, [name]: value }))
  }

  const handleDeferralChangeByPath = (name, value) => {
    setDeferral(prev => setByPath(prev, name, value))
  }

  const handleDeferralDateChange = (name, date) => {
    log('handleDeferralDateChange', { name, date })
    setDeferral(prev => setByPath(prev, name, date))
  }

  const clearDeferral = () => setDeferral(deferralInitialState)

  const submitSuspensionApplication = async (workListId, onClose = () => {}) => {
    try {
      setLoading(true)
      const fd = buildSuspensionApplicationFormData(workListId)
      if (!fd) {
        log('submitSuspensionApplication:skip:noFormData', { workListId })
        enqueueSnackbar('Неверный тип заявки', { variant: 'warning' })
        return
      }
      log('submitSuspensionApplication:submit', { workListId })
      const res = await CollectionService.createApplicationMultipart(fd)
      if (res?.data) {
        log('submitSuspensionApplication:success')
        enqueueSnackbar('Заявка отправлена', { variant: 'success' })
        onClose()
        if (id && autoFetch) await getApplicationById(id)
      } else {
        enqueueSnackbar(`Что-то пошло не так, статус ${res?.status}`, { variant: 'error' })
      }
    } catch (e) {
      err('submitSuspensionApplication:error', e)
      enqueueSnackbar('Ошибка при отправке заявки', { variant: 'error' })
    } finally {
      setLoading(false)
      log('submitSuspensionApplication:done', { workListId })
    }
  }
  const submitBuybackApplication = async (workListId, onClose = () => {}) => {
    try {
      setLoading(true)
      const fd = buildBuybackApplicationFormData(workListId)
      if (!fd) {
        log('submitBuybackApplication:skip:noFormData', { workListId })
        enqueueSnackbar('Неверный тип заявки', { variant: 'warning' })
        return
      }
      log('submitBuybackApplication:submit', { workListId })
      const res = await CollectionService.createApplicationMultipart(fd)
      if (res?.data) {
        log('submitBuybackApplication:success')
        enqueueSnackbar('Заявка (Buyback) отправлена', { variant: 'success' })
        onClose()
        if (id && autoFetch) await getApplicationById(id)
      } else {
        enqueueSnackbar(`Что-то пошло не так, статус ${res?.status}`, { variant: 'error' })
      }
    } catch (e) {
      err('submitBuybackApplication:error', e)
      enqueueSnackbar('Ошибка при отправке заявки (Buyback)', { variant: 'error' })
    } finally {
      setLoading(false)
      log('submitBuybackApplication:done', { workListId })
    }
  }
  const submitWriteOffApplication = async (workListId, onClose = () => {}) => {
    try {
      setLoading(true)
      const fd = buildWriteOffApplicationFormData(workListId)
      if (!fd) {
        log('submitWriteOffApplication:skip:noFormData', { workListId })
        enqueueSnackbar('Неверный тип заявки', { variant: 'warning' })
        return
      }
      log('submitWriteOffApplication:submit', { workListId })
      const res = await CollectionService.createApplicationMultipart(fd)
      if (res?.data) {
        log('submitWriteOffApplication:success')
        enqueueSnackbar('Заявка (Write-off) отправлена', { variant: 'success' })
        onClose()
        if (id && autoFetch) await getApplicationById(id)
      } else {
        enqueueSnackbar(`Что-то пошло не так, статус ${res?.status}`, { variant: 'error' })
      }
    } catch (e) {
      err('submitWriteOffApplication:error', e)
      enqueueSnackbar('Ошибка при отправке заявки (Write-off)', { variant: 'error' })
    } finally {
      setLoading(false)
      log('submitWriteOffApplication:done', { workListId })
    }
  }

  const applicationStore = {
    loading,
    pagination,
    applications,
    application,
    files,
    resubmitErrors,
    deferral
  }

  const applicationStoreFunc = {
    setPagination,
    handlePaginationChange,
    getApplications,
    getApplicationById,

    resubmitApplication,
    buildResubmitFormFromApplication,
    validateResubmitFields,
    clearResubmitError,
    clearAllResubmitErrors,
    approveApplication,
    rejectApplication,

    handleDeferralChange,
    handleDeferralChangeByPath,
    handleDeferralDateChange,
    clearDeferral,
    submitSuspensionApplication,
    submitBuybackApplication,
    submitWriteOffApplication,
    setDeferral,

    setApplication,
    setApplications,
    setFiles,
    setResubmitErrors
  }

  return (
    <ApplicationStoreContext.Provider value={{ applicationStore, applicationStoreFunc }}>
      {children}
    </ApplicationStoreContext.Provider>
  )
}

export default ApplicationStoreProvider
